const CATEGORICAL = ["select", "multiple_select"];
const NUMERIC     = ["number"];

const norm  = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();

// Parse a survey_questions.options value (JSON string OR array) into a clean list.
export function parseOptions(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((o) => o !== null && o !== undefined && o !== "").map(norm);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr)
        ? arr.filter((o) => o !== null && o !== undefined && o !== "").map(norm)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? null : n;
}

// Return the answer value(s) a response holds for a given question id.
// multiple_select yields an array; everything else a single-element array.
function answerValues(resp, questionId, type) {
  const a = (resp.answers || []).find(
    (x) => Number(x.question_id) === Number(questionId)
  );
  if (!a) return [];

  if (Array.isArray(a.answer_array) && a.answer_array.length) {
    return a.answer_array.map(norm).filter(Boolean);
  }
  const raw = a.answer;
  if (raw === null || raw === undefined || norm(raw) === "") return [];

  if (type === "multiple_select") {
    return String(raw).split(",").map(norm).filter(Boolean);
  }
  return [norm(raw)];
}

// Build the ordered question list for a survey. Prefer the survey's declared
// questions (survey.questions from the API); fall back to deriving from answers.
export function collectQuestions(responses, providedQuestions) {
  const shape = (q, id) => ({
    id: Number(id ?? q.id),
    question: norm(q.question),
    type: q.type,
    order: q.order ?? 9999,
    option_text: q.option_text ?? null,
    parent_id: q.parent_id ?? null,
    options: parseOptions(q.options),
  });

  if (Array.isArray(providedQuestions) && providedQuestions.length) {
    return providedQuestions
      .map((q) => shape(q))
      .sort((a, b) => a.order - b.order || a.id - b.id);
  }

  const seen = new Map();
  (responses || []).forEach((r) =>
    (r.answers || []).forEach((a) => {
      const q = a.question;
      if (!q) return;
      const id = Number(a.question_id);
      if (!seen.has(id)) seen.set(id, shape(q, id));
    })
  );
  return [...seen.values()].sort((a, b) => a.order - b.order || a.id - b.id);
}

// Analyse a single question across all responses.
function analyzeQuestion(question, responses) {
  const meta = {
    id: question.id,
    question: question.question,
    type: question.type,
    order: question.order,
    option_text: question.option_text,
    parent_id: question.parent_id,
  };

  // ── Categorical: select / multiple_select ────────────────────────────────
  if (CATEGORICAL.includes(question.type)) {
    const counts = {};
    // Seed declared options at 0 so unselected options still appear.
    question.options.forEach((o) => {
      counts[o] = 0;
    });

    let answered = 0;
    responses.forEach((r) => {
      const vals = answerValues(r, question.id, question.type);
      if (vals.length) answered += 1;
      vals.forEach((v) => {
        counts[v] = (counts[v] || 0) + 1;
      });
    });

    const options = Object.entries(counts)
      .map(([label, count]) => ({
        label,
        count,
        // % of respondents who answered this question (multi-select can exceed 100%)
        pct: answered ? +((count / answered) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      ...meta,
      kind: "categorical",
      multi: question.type === "multiple_select",
      answered,
      options,
      top: options.find((o) => o.count > 0)?.label ?? null,
    };
  }

  // ── Numeric ───────────────────────────────────────────────────────────────
  if (NUMERIC.includes(question.type)) {
    const nums = [];
    responses.forEach((r) =>
      answerValues(r, question.id, question.type).forEach((v) => {
        const n = parseNumber(v);
        if (n !== null) nums.push(n);
      })
    );
    const count = nums.length;
    const sum = nums.reduce((a, b) => a + b, 0);
    return {
      ...meta,
      kind: "numeric",
      count,
      sum,
      avg: count ? +(sum / count).toFixed(2) : 0,
      min: count ? Math.min(...nums) : 0,
      max: count ? Math.max(...nums) : 0,
    };
  }

  // ── Textual: text / textarea ──────────────────────────────────────────────
  const counts = {};
  let answered = 0;
  const samples = [];
  responses.forEach((r) => {
    answerValues(r, question.id, question.type).forEach((v) => {
      if (!v) return;
      answered += 1;
      counts[v] = (counts[v] || 0) + 1;
      if (samples.length < 5) samples.push(v);
    });
  });
  const top = Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return {
    ...meta,
    kind: "text",
    answered,
    distinct: Object.keys(counts).length,
    top,
    samples,
  };
}

// Decide whether this survey is a "retail / outlet" survey (enables the
// brand/spend/carton add-ons) or a generic one.
export function detectRetail(questions) {
  return questions.some((q) => {
    const t = lower(q.question);
    return (
      t.includes("outlet") ||
      (q.type === "multiple_select" && /brand/.test(t)) ||
      (q.type === "number" && /(spend|carton|average sales|monthly sales|sales in carton)/.test(t))
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point.
//   responses         : array of survey_responses, each with .answers[] that
//                       carry { question_id, question:{...}, answer, answer_array }
//   providedQuestions : optional survey.questions array (preferred source)
// ─────────────────────────────────────────────────────────────────────────────
export function buildSurveyAnalytics(responses = [], providedQuestions = null) {
  const questions = collectQuestions(responses, providedQuestions);

  // Ignore child/conditional questions in the headline per-question view when
  // they duplicate a parent (they still get analysed, just flagged).
  const perQuestion = questions.map((q) => analyzeQuestion(q, responses));

  // Generic meta useful for every survey.
  const regionCounts = {};
  const enumeratorCounts = {};
  const dailyCounts = {};
  responses.forEach((r) => {
    const region = r.region?.name || "Unassigned";
    const agent = r.user?.name || r.enumerator_name || "Anonymous";
    const day = r.created_at ? new Date(r.created_at).toLocaleDateString() : "—";
    regionCounts[region] = (regionCounts[region] || 0) + 1;
    enumeratorCounts[agent] = (enumeratorCounts[agent] || 0) + 1;
    dailyCounts[day] = (dailyCounts[day] || 0) + 1;
  });

  const isRetail = detectRetail(questions);

  return {
    total: responses.length,
    surveyKind: isRetail ? "retail" : "generic",
    isRetail,
    questions,
    perQuestion,
    regionCounts,
    enumeratorCounts,
    dailyCounts,
  };
}

export default buildSurveyAnalytics;